/**
 * Challenges section - self-loading with its own data fetching
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useLocalized } from '../../../Hooks/useLocalized';
import { useLocalizedImage } from '../../../Hooks/useLocalizedMedia';
import { getMediaMedium } from '../../../Model/Media/Media';
import { useCurrentChallenges } from '../Hooks/useMotivationData';
import { RouteChallenge } from '../../../Model/Engagement/RouteChallenge/RouteChallenge';
import { IndividualChallenge } from '../../../Model/Engagement/IndividualChallenge/IndividualChallenge';
import { Theme } from '../../../Theme/Theme';
import { Translation } from '../../../Components/Translation';

type ChallengeItem = RouteChallenge | IndividualChallenge;

const Section = styled.section``;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const SectionTitle = styled.div`
  font-weight: 700;
  font-size: 24px;
  line-height: 1.3;
  color: ${Theme.Colors.textColor};
`;

const SeeAllLink = styled.button`
  background: none;
  border: none;
  color: ${Theme.Colors.primary};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 8px;
  transition: background 0.2s;

  &:hover {
    background: ${Theme.Colors.primary}10;
  }
`;

const ChallengeScroll = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding-bottom: 8px;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: ${Theme.Colors.lightestGrey};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${Theme.Colors.lighterGrey};
    border-radius: 3px;
  }

  @media (min-width: 992px) {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    overflow-x: visible;
  }
`;

const ChallengeCard = styled.div`
  flex: 0 0 280px;
  scroll-snap-align: start;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
  }

  @media (min-width: 992px) {
    flex: 1;
  }
`;

const ChallengeImage = styled.div<{ imageUrl?: string }>`
  height: 120px;
  background: ${(props) =>
    props.imageUrl
      ? `url(${props.imageUrl})`
      : `linear-gradient(135deg, ${Theme.Colors.primary} 0%, ${Theme.Colors.primaryDark} 100%)`};
  background-size: cover;
  background-position: center;
  position: relative;
`;

const ChallengeBadge = styled.span<{ variant: 'running' | 'upcoming' | 'ended' }>`
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${(props) => {
    switch (props.variant) {
      case 'running':
        return '#10B981';
      case 'upcoming':
        return '#F59E0B';
      case 'ended':
        return Theme.Colors.charcoal;
      default:
        return Theme.Colors.primary;
    }
  }};
  color: white;
`;

const ChallengeContent = styled.div`
  padding: 14px;
`;

const ChallengeName = styled.h3`
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 6px 0;
  color: ${Theme.Colors.textColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChallengeMeta = styled.div`
  font-size: 12px;
  color: ${Theme.Colors.warmGrey};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 20px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  color: ${Theme.Colors.warmGrey};
`;

// Extracted component to avoid hooks-in-loops
const ChallengeSectionCard: React.FC<{
  challenge: ChallengeItem;
  onClick: (challenge: ChallengeItem) => void;
}> = observer(({ challenge, onClick }) => {
  const { t } = useTrans();
  const name = useLocalized(challenge.name);
  const image = useLocalizedImage(challenge.images);
  const locationsCount = challenge.locations?.length ?? 0;
  const status: 'running' | 'upcoming' | 'ended' = challenge.ended
    ? 'ended'
    : challenge.started
      ? 'running'
      : 'upcoming';

  return (
    <ChallengeCard onClick={() => onClick(challenge)}>
      <ChallengeImage imageUrl={image ? getMediaMedium(image) : undefined}>
        <ChallengeBadge variant={status}>
          {status === 'running'
            ? t('motivation.active', { defaultValue: 'Active' })
            : status === 'upcoming'
              ? t('motivation.soon', { defaultValue: 'Soon' })
              : t('motivation.ended', { defaultValue: 'Ended' })}
        </ChallengeBadge>
      </ChallengeImage>
      <ChallengeContent>
        <ChallengeName>{name}</ChallengeName>
        <ChallengeMeta>
          <span>📍 {locationsCount}</span>
          <span>·</span>
          <span>
            {status === 'running'
              ? dayjs(challenge.endDateTime).fromNow(true) + ' left'
              : dayjs(challenge.startDateTime).fromNow()}
          </span>
        </ChallengeMeta>
      </ChallengeContent>
    </ChallengeCard>
  );
});

export const ChallengesSection: React.FC = observer(() => {
  const { t } = useTrans();
  const navigate = useNavigate();
  const { data } = useCurrentChallenges();

  const allChallenges = [...(data?.routeChallenges ?? []), ...(data?.individualChallenges ?? [])].sort((a, b) => {
    const aRunning = a.started && !a.ended;
    const bRunning = b.started && !b.ended;
    if (aRunning !== bRunning) return aRunning ? -1 : 1;
    return a.startDateTime.getTime() - b.startDateTime.getTime();
  });

  const displayChallenges = allChallenges.slice(0, 3);

  const handleChallengeClick = (challenge: ChallengeItem) => {
    navigate(`/motivation/challenge/${challenge.id}`);
  };

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>
          <Translation
            i18nKey="motivation.challenges"
            defaultValue={`<span style="color: #00dda2">Your</span> challenges`}
          />
        </SectionTitle>
        <SeeAllLink onClick={() => navigate('/motivation/challenges')}>
          {t('motivation.seeAll', { defaultValue: 'See all' })}
        </SeeAllLink>
      </SectionHeader>
      {displayChallenges.length > 0 ? (
        <ChallengeScroll>
          {displayChallenges.map((challenge) => (
            <ChallengeSectionCard key={challenge.id} challenge={challenge} onClick={handleChallengeClick} />
          ))}
        </ChallengeScroll>
      ) : (
        <EmptyState>
          {t('motivation.noChallenges', { defaultValue: 'No challenges available right now' })}
        </EmptyState>
      )}
    </Section>
  );
});
